<template>
  <LayoutOverlay class="project-details" @closeOverlay="emits('projectDetailsClose')">
    <div class="card">
      <div class="header">
        <div class="title">
          <span v-if="project.externalId && internal"> {{ project.externalId }} - </span>
          {{ project.name }}
        </div>
        <ButtonText
          id="project-exit"
          class="text-button"
          :iconLeft="iconXSmall"
          size="extra-small"
          @onClicked="$emit('projectDetailsClose')"
        />
      </div>
      <div class="horizontal-divider"></div>
      <div class="badges">
        <BadgeSquare :badgeText="badgeType" :size="'small'" :color="'primary'" />
        <BadgeSquare
          :badgeText="PROJECT_STATUS[project.status][locale]"
          :size="'small'"
          :style="{ backgroundColor: BADGE_COLORS[project.status] }"
        />
      </div>
      <div class="body">
        <div class="body-info">
          <div class="info">
            <div v-if="internal">
              <span class="text-bold"> {{ t('general__data__projectnumber') }}: </span>
              <span v-if="project.externalId" class="text-regular">
                {{ project.externalId }}
              </span>
              <span v-else class="text-regular"> - </span>
            </div>
            <div>
              <span class="text-bold"> {{ t('general__text__description') }}: </span>
              <span v-if="project.name" class="text-regular">
                {{ project.name }}
              </span>
              <span v-else class="text-regular"> - </span>
            </div>
            <div v-if="internal">
              <span class="text-bold"> {{ t('general__data__project-leader') }}: </span>
              <span v-if="project.projectManager" class="text-regular">
                {{ project.projectManager }}
              </span>
              <span v-else class="text-regular"> - </span>
            </div>
            <div v-if="internal">
              <span class="text-bold"> {{ t('general__text__budget') }}: </span>
              <span v-if="project.budget" class="text-regular">
                {{
                  formatter(parseFloat(project.budget.toString()) / 1000, locale, {
                    format: { type: 'intl-number', maximumFractionDigits: 1 }
                  })
                }}
                tkr
              </span>
              <span v-else class="text-regular"> - </span>
            </div>
            <div class="building-info">
              <span class="text-bold"> {{ t('general__text__building') }}: </span>
              <ButtonText
                v-if="!correctBuilding"
                class="building"
                :buttonText="constructionName"
                :iconLeft="iconArrowUpRight"
                size="extra-small"
                @onClicked="moveTo()"
              />
              <span v-else class="text-regular">
                {{ constructionName }}
              </span>
            </div>
            <div :class="{ external: !internal, internal: internal }">
              <div class="time-div">
                <div>
                  <span class="text-paragraf"> {{ t('general__text__time-range') }}: </span>
                </div>
                <div>
                  <span class="text-bold"> {{ t('general__text__move-in-date') }}: </span>
                  <span v-if="project.moveDate" class="text-regular">
                    {{ project.moveDate }}
                  </span>
                  <span v-else> - </span>
                </div>
                <div>
                  <span class="text-bold"
                    >{{ t('general__text__start-date') }}:
                    <span v-if="project.startDate" class="text-regular"
                      >{{ project.startDate }}
                    </span>
                    <span v-else class="text-regular"> - </span></span
                  >
                </div>
                <div>
                  <span class="text-bold"
                    >{{ t('general__text__end-date') }}:
                    <span v-if="project.endDate" class="text-regular">{{ project.endDate }} </span>
                    <span v-else class="text-regular"> - </span>
                  </span>
                </div>
              </div>
              <div>
                <div>
                  <span class="text-paragraf"> {{ t('general__text__size') }}: </span>
                </div>
                <div>
                  <span class="text-bold"
                    >{{ t('general__text__GFA-Renovation') }}:
                    <span v-if="project.btaRebuild" class="text-regular"
                      >{{ project.btaRebuild }} m²</span
                    >
                    <span v-else class="text-regular"> - </span>
                  </span>
                </div>
                <div>
                  <span class="text-bold"
                    >{{ t('general__text__GFA-New-Construction') }}:
                    <span v-if="project.btaNewConstruction" class="text-regular"
                      >{{ project.btaNewConstruction }} m²</span
                    >
                    <span v-else class="text-regular"> - </span>
                  </span>
                </div>
                <div>
                  <span class="text-bold"
                    >{{ t('general__text__GFA-Extension') }}:
                    <span v-if="project.btaExtension" class="text-regular"
                      >{{ project.btaExtension }} m²</span
                    >
                    <span v-else class="text-regular"> - </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="internal" class="presentation">
            <span class="text-bold"> {{ t('general__text__presentation') }}: </span>
            <br />
            <span v-if="project.description" class="text-regular">
              {{ project.description }}
            </span>
            <span v-else class="text-regular"> - </span>
          </div>
        </div>
      </div>
    </div>
  </LayoutOverlay>
</template>

<script lang="ts" setup>
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { PROJECT_TYPE, PROJECT_STATUS, BADGE_COLORS } from '@/api-client/models/project/Constants'
import { useStore } from 'vuex'
import { Project } from '@/api-client/models/project/Contracts'

import useNavigateTo from '@/composables/useNavigateTo'
import { formatter } from '../formatter'
import iconXSmall from '@/design-system/assets/icons/Icon-X-Small.svg'
import iconArrowUpRight from '@/design-system/assets/icons/Icon-Arrow-up-right.svg'
interface EMITS {
  (e: 'projectDetailsClose'): void
}
type Props = {
  project: Project
  internal: boolean
  selectedBadgeColor: string
}
const props = defineProps<Props>()
const { locale, t } = useI18n()
const vuex = useStore()
const { navigateTo } = useNavigateTo()

const internal = computed(() => {
  if (props.internal) {
    return true
  } else {
    return false
  }
})
const badgeType = computed(() => {
  return PROJECT_TYPE[props.project.type][locale.value]
})
const buildings = computed(() => vuex.getters.allBuildings.map((x) => x.properties))
const selectedBuilding = computed(() => vuex.getters['mapview/selectedBuildingId'])
const correctBuilding = computed(() => {
  if (props.project.constructionId === selectedBuilding.value) {
    return true
  } else {
    return false
  }
})

const constructionName = computed(() => {
  const constructionIdToFind = props.project.constructionId
  const construction = buildings.value.find((item) => item.id === constructionIdToFind)
  return construction?.name
})

function moveTo() {
  if (props.project.constructionId) {
    navigateTo(props.project.constructionId)
    emits('projectDetailsClose')
  }
}
const emits = defineEmits<EMITS>()
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss' as mixins;
.card {
  display: flex;
  width: 800px;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
  background-color: white;
  border-radius: 8px;
}
.project-details {
  display: flex;
  justify-content: center;
  align-items: center;
}
.body {
  width: 100%;
}
.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.text-button {
  width: 24px;
  height: 24px;
}
.title {
  margin: 0;
  @include font-mixins.desktop-bold-h4;
  color: variables.$color-greyscale-content;
}
.horizontal-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.badges {
  display: flex;
  gap: 8px;
  flex-direction: row;
}
.body-info {
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
}

.info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.presentation {
  @include mixins.scrollbar-vertical;
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
}
.external {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 16px;
}
.internal {
  margin-top: 16px;
}
.time-div {
  width: 360px;
  margin-bottom: 16px;
}

.text-bold {
  @include font-mixins.desktop-bold-paragraph;
  color: variables.$color-greyscale-content;
}
.text-regular {
  @include font-mixins.desktop-regular-paragraph;
  color: variables.$color-greyscale-content;
}
.text-paragraf {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-secondary;
}
.building-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
}
.building {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-primary;
  display: flex;
  flex-direction: row;
  gap: 4px;
  cursor: pointer;
  max-height: 10px;
}
</style>
